import { Skeleton } from '@mantine/core';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Line, LineChart, ResponsiveContainer, XAxis } from 'recharts';
import { OverviewCard as OverviewCardCommon } from '../../../components/overview-card/overview-card';
import { useFinancialsStore } from '../../../store/financials-store/financials-store';
import type { CostVersusOutputMeasures } from '../../../store/financials-store/financials-store.type';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useCostVersusOutput } from '../financials.hooks';
import {
  buildTrendLines,
  measureColorMap,
  transformCostVersusOutputData,
} from './cost-versus-output/cost-versus-output.helpers';
import type { DataPoint } from './overview.type';

function Overview() {
  const { t } = useTranslation();
  const portfolio = useGlobalStore((state) => state.portfolio);
  const startDate = useFinancialsStore((state) => state.startDate);
  const endDate = useFinancialsStore((state) => state.endDate);
  const team = useFinancialsStore((state) => state.team);
  const board = useFinancialsStore((state) => state.board);

  const { data, query } = useCostVersusOutput(
    dayjs(startDate).toDate(),
    dayjs(endDate).toDate(),
    portfolio?.id || null,
    team,
    board,
  );
  const chartData = transformCostVersusOutputData(data);

  const chartDataWithTrendLines = buildTrendLines(chartData);

  return (
    <div style={{ display: 'flex', gap: 16 }}>
      {query.isPending ? (
        <>
          <Skeleton height={150} radius={10} />
          <Skeleton height={150} radius={10} />
          <Skeleton height={150} radius={10} />
          <Skeleton height={150} radius={10} />
          <Skeleton height={150} radius={10} />
        </>
      ) : (
        <>
          <OverviewCard
            chartData={chartDataWithTrendLines}
            dateKey="cost"
            color={measureColorMap.cost}
            iconName="payments"
            label={t('financials.cost')}
          />
          <OverviewCard
            chartData={chartDataWithTrendLines}
            dateKey="cost_per_task"
            color={measureColorMap.cost_per_task}
            iconName="payments"
            label={t('financials.overview.cost_per_task', { work_unit: t('common.work_unit') })}
          />
          <OverviewCard
            chartData={chartDataWithTrendLines}
            dateKey="daily_burn"
            color={measureColorMap.daily_burn}
            iconName="stacked_line_chart"
            label={t('financials.daily_spend')}
          />
          <OverviewCard
            chartData={chartDataWithTrendLines}
            dateKey="completed_tasks"
            color={measureColorMap.completed_tasks}
            iconName="switch_access_shortcut"
            label={t('financials.throughput')}
          />
          <OverviewCard
            chartData={chartDataWithTrendLines}
            dateKey="committed_tasks"
            color={measureColorMap.committed_tasks}
            iconName="add_task"
            label={t('financials.tasks_committed', { work_units: t('common.work_units') })}
          />
        </>
      )}
    </div>
  );
}

function OverviewCard({
  chartData,
  dateKey,
  color,
  iconName,
  label,
}: {
  chartData: DataPoint[];
  dateKey: CostVersusOutputMeasures;
  color: string;
  iconName: string;
  label: string;
}) {
  const content = (
    <ResponsiveContainer width="100%" height={100}>
      <LineChart
        data={chartData}
        margin={{ top: 16, bottom: 16 }}
        tabIndex={-1}
        style={{ pointerEvents: 'none' }}
        aria-label={`Chart showing ${label} over time.`}
      >
        <XAxis dataKey="date" hide={true} />
        <Line type="natural" dataKey={dateKey} stroke={color} strokeWidth={2} dot={false} />
        <Line
          type="natural"
          dataKey={`${dateKey}_trend`}
          stroke={color}
          strokeWidth={1}
          strokeDasharray="5 5"
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );

  return <OverviewCardCommon title={label} icon={iconName} content={content} />;
}

export { Overview };
